import {
  Badge,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Resources from "src/@types/resources";
import { useGetPatientTasksQuery } from "src/api/services/tasksApi";
import HolaVidaLogo from "src/assets/HolaVidaLogo";
import AccountIcon from "src/assets/icons/AccountIcon";
import NotificationBellIcon from "src/assets/icons/NotificationBellIcon";
import SelectLanguage from "src/components/SelectLanguage";
import { Paths } from "src/constants/paths";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import { useIsMobile } from "src/hooks/useIsMobile";

const pages: {
  name: keyof Resources["translation"]["navBar"];
  link: string;
}[] = [
  {
    name: `home`,
    link: Paths.PATIENT_DASHBOARD,
  },
  {
    name: `messaging`,
    link: Paths.MESSAGING,
  },
  {
    name: `glp-1`,
    link: Paths.GLP1,
  },
  {
    name: `care`,
    link: Paths.CARE,
  },
  {
    name: `shop`,
    link: Paths.SHOP,
  },
  {
    name: `referal`,
    link: Paths.REFERRALS,
  },
];

const DashboardNavBar = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { data } = useGetPatientTasksQuery();
  const { t } = useTranslation();
  const page = "navBar";
  const subscriptions = usePatientSubscriptions((sub) => sub);

  const tasks = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data;
  }, [data]);

  const isActive = useCallback(
    (path: string | undefined) => {
      const fullPath = pathname + search;
      return path && fullPath.includes(path);
    },
    [pathname, search],
  );

  const handleNavigateToTask = useCallback(async () => {
    if (!tasks[0].task_link) return;

    const link = tasks[0].task_link;

    if (link) {
      navigate(link, { replace: true });
    }
  }, [tasks, navigate]);

  const unreadMessageTasks = useMemo(
    () => tasks.filter((t) => t.task_type === "UNREAD_MESSAGE"),
    [tasks],
  );

  const showMessageBadge = useCallback(
    (name: string) => {
      return name !== "messaging" || unreadMessageTasks.length <= 0;
    },
    [unreadMessageTasks.length],
  );

  const badgeMark = (
    <Typography fontWeight={700} color="#ffffff">
      !
    </Typography>
  );

  const filteredPages = useMemo(() => {
    const page =
      subscriptions.length === 0 ||
      subscriptions.every((s) => s.status === "canceled")
        ? "messaging"
        : "glp-1";

    return pages.filter((p) => p.name !== page);
  }, [subscriptions]);

  return !isMobile ? (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      pl={6}
      pr={6}
      pt={1}
      pb={1}
    >
      <HolaVidaLogo
        data-testid="logo"
        style={{ width: "140px", height: "auto" }}
      />
      <Stack direction="row" gap={5}>
        {filteredPages.map((item, index) => {
          const { name, link } = item;
          const active = isActive(link);

          return (
            <Link
              to={link}
              style={{
                textDecoration: "none",
                color: "#15202b",
                display: "inline-flex",
                position: "relative",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "0 8px",
              }}
              key={index}
            >
              <Stack direction="column" alignItems="center">
                <Badge
                  color="secondary"
                  badgeContent={badgeMark}
                  invisible={showMessageBadge(name)}
                >
                  <Typography fontWeight={active ? 600 : 400}>
                    {t(`${page}.${name}`)}
                  </Typography>
                </Badge>
                {active ? (
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      top: 50,
                      height: "4px",
                      bgcolor: "#00B6B9",
                    }}
                  />
                ) : null}
              </Stack>
            </Link>
          );
        })}
      </Stack>
      <Stack direction="row" alignItems="center" gap={2}>
        <Button variant="outlined" component="a" href="sms:+13053955016">
          {t(`${page}.text-us`, "Text us")}
        </Button>
        <SelectLanguage />
        {tasks.length === 0 ? null : (
          <IconButton
            size="small"
            sx={{ height: "fit-content" }}
            onClick={handleNavigateToTask}
          >
            <NotificationBellIcon />
          </IconButton>
        )}
        <IconButton
          size="medium"
          onClick={() =>
            navigate(`${Paths.MY_ACCOUNT}/${Paths.MY_ACCOUNT_OVERVIEW}`)
          }
        >
          <AccountIcon
            sx={{ height: "40px", width: "40px", color: "#ffffff" }}
          />
        </IconButton>
      </Stack>
    </Box>
  ) : (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={1}
    >
      <SelectLanguage />
      <Stack direction="row" alignItems="center" gap={1}>
        <Button variant="outlined" component="a" href="sms:+13053955016">
          {t(`${page}.text-us`, "Text us")}
        </Button>
        <IconButton size="small" sx={{ height: "fit-content" }}>
          <NotificationBellIcon />
        </IconButton>
        <IconButton size="large" onClick={() => navigate(Paths.MY_ACCOUNT)}>
          <AccountIcon
            sx={{ height: "40px", width: "40px", color: "#ffffff" }}
          />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default DashboardNavBar;
